import { useEffect, useState } from "react";
import axios from "axios";
import { useVoiceGuide } from "@/contexts/VoiceAssistantContext";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Loader2, Receipt as ReceiptIcon, CheckCircle2, XCircle, Clock, Edit3, RefreshCw, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiErrorMessage } from "@/utils/apiErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal where the client uploads a new payment request OR edits a rejected/pending one.
 *
 * Props:
 *   open (bool)
 *   onClose () => void
 *   onSubmitted () => void    // called after successful submit/edit
 *   authHeader () => object
 *   services []               // service catalogue
 *   editing (object|null)     // if set, edit existing request
 */
export const PaymentRequestModal = ({ open, onClose, onSubmitted, authHeader, services = [], editing = null }) => {
  useVoiceGuide("paymodal", "Vamos a registrar tu pago. Elige el servicio, indica si es abono o pago completo, el monto y la moneda, y sube la foto de tu comprobante. Al terminar toca Enviar; Cristopher lo aprobará y te avisaré.", !!open);
  const [serviceId, setServiceId] = useState("");
  const [serviceLabel, setServiceLabel] = useState("");
  const [paymentType, setPaymentType] = useState("abono"); // abono | completo
  const [amount, setAmount] = useState("");
  const [agreedPrice, setAgreedPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset / pre-fill on open
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setServiceId(editing.service_id || "");
      setServiceLabel(editing.service_label || "");
      setPaymentType(editing.payment_type || "abono");
      setAmount(editing.amount?.toString() || "");
      setAgreedPrice(editing.agreed_price?.toString() || "");
      setNotes(editing.notes || "");
      setProofFile(null);
      setProofPreview(null);
    } else {
      setServiceId("");
      setServiceLabel("");
      setPaymentType("abono");
      setAmount("");
      setAgreedPrice("");
      setNotes("");
      setProofFile(null);
      setProofPreview(null);
    }
  }, [open, editing]);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Máximo 5MB por comprobante");
      return;
    }
    setProofFile(f);
    const r = new FileReader();
    r.onloadend = () => setProofPreview(r.result);
    r.readAsDataURL(f);
  };

  const onServicePick = (id) => {
    const s = services.find((x) => x.id === id);
    setServiceId(id);
    setServiceLabel(s ? `${s.name}${s.price ? ` (precio referencia: $${s.price})` : ""}` : id);
    if (s?.price && !agreedPrice) setAgreedPrice(String(s.price));
  };

  const submit = async () => {
    if (!editing && !serviceId) return toast.error("Elige un servicio del catálogo");
    if (!amount || Number(amount) <= 0) return toast.error("Indica el monto que pagaste");
    if (paymentType === "abono" && (!agreedPrice || Number(agreedPrice) <= 0)) {
      return toast.error("Para abonos, indica el precio total acordado del servicio");
    }
    // For new requests we ALWAYS need a proof. For edits we only need it if user wants to replace.
    if (!editing && !proofFile) return toast.error("Sube el comprobante del pago");

    setSubmitting(true);
    try {
      // Convert file to base64 (just the data part, no `data:` prefix)
      let proof_b64 = null;
      let proof_mime = null;
      if (proofFile) {
        proof_b64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onloadend = () => {
            const result = (r.result || "").toString();
            resolve(result.split(",")[1] || "");
          };
          r.onerror = reject;
          r.readAsDataURL(proofFile);
        });
        proof_mime = proofFile.type || "image/jpeg";
      }
      if (editing) {
        await axios.patch(
          `${API}/users/payment-requests/${editing.id}`,
          {
            payment_type: paymentType,
            amount: Number(amount),
            agreed_price: Number(agreedPrice) || null,
            notes,
            ...(proof_b64 ? { proof_b64, proof_mime } : {}),
          },
          { headers: authHeader() }
        );
        toast.success("Solicitud actualizada. Esperando aprobación.");
      } else {
        await axios.post(
          `${API}/users/payment-requests`,
          {
            service_id: serviceId,
            service_label: serviceLabel,
            payment_type: paymentType,
            amount: Number(amount),
            agreed_price: Number(agreedPrice) || null,
            notes,
            proof_b64,
            proof_mime,
          },
          { headers: authHeader() }
        );
        toast.success("Comprobante enviado. Cristopher lo revisará pronto.");
      }
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Error al enviar la solicitud"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md flex items-center justify-center px-3 py-4 sm:px-4 sm:py-6"
        data-testid="payment-request-modal"
      >
        <motion.div
          initial={{ scale: 0.96, y: 12, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className="relative w-full max-w-lg bg-gradient-to-br from-[#0a0510] via-[#150820] to-black border border-[#d4af37]/30 rounded-2xl shadow-[0_0_60px_rgba(212,175,55,0.2)] max-h-[90vh] overflow-y-auto"
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
            data-testid="payment-request-close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="p-5 sm:p-6">
            <h2 className="font-serif text-xl sm:text-2xl text-white flex items-center gap-2 mb-1">
              <ReceiptIcon className="w-5 h-5 text-[#d4af37]" />
              {editing ? "Editar solicitud de pago" : "Solicitar registro de pago"}
            </h2>
            <p className="text-xs text-neutral-400 mb-4">
              Sube el comprobante de tu transferencia y Cristopher lo aprobará desde su panel.
            </p>

            {!editing && (
              <div className="mb-3">
                <Label className="text-xs uppercase tracking-widest text-[#d4af37]">Servicio</Label>
                <select
                  data-testid="pr-service-select"
                  value={serviceId}
                  onChange={(e) => onServicePick(e.target.value)}
                  className="w-full mt-1.5 bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value="">— Elige un servicio del catálogo —</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.price ? ` — $${s.price} ${s.currency || "MXN"}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {editing && (
              <div className="mb-3 text-xs text-neutral-300 bg-black/40 border border-white/5 rounded-lg p-2">
                <span className="text-neutral-500">Servicio: </span>
                <span className="text-[#d4af37]">{editing.service_label}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                type="button"
                data-testid="pr-type-abono"
                onClick={() => setPaymentType("abono")}
                className={`px-3 py-2.5 rounded-lg border text-xs uppercase tracking-widest ${
                  paymentType === "abono"
                    ? "bg-[#d4af37] text-black border-[#d4af37] font-bold"
                    : "bg-black/40 border-white/10 text-neutral-300 hover:border-[#d4af37]/40"
                }`}
              >
                Abono parcial
              </button>
              <button
                type="button"
                data-testid="pr-type-completo"
                onClick={() => setPaymentType("completo")}
                className={`px-3 py-2.5 rounded-lg border text-xs uppercase tracking-widest ${
                  paymentType === "completo"
                    ? "bg-[#d4af37] text-black border-[#d4af37] font-bold"
                    : "bg-black/40 border-white/10 text-neutral-300 hover:border-[#d4af37]/40"
                }`}
              >
                Pago completo
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#d4af37]">Monto que pagas</Label>
                <Input
                  type="number"
                  data-testid="pr-amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className="mt-1.5 bg-black/60 border-white/10"
                />
              </div>
              {paymentType === "abono" && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#d4af37]">Precio total acordado</Label>
                  <Input
                    type="number"
                    data-testid="pr-agreed"
                    inputMode="decimal"
                    value={agreedPrice}
                    onChange={(e) => setAgreedPrice(e.target.value)}
                    placeholder="Total del servicio"
                    className="mt-1.5 bg-black/60 border-white/10"
                  />
                </div>
              )}
            </div>

            <div className="mb-3">
              <Label className="text-xs uppercase tracking-widest text-[#d4af37]">Notas (opcional)</Label>
              <Textarea
                data-testid="pr-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Banco, referencia, observaciones…"
                className="mt-1.5 bg-black/60 border-white/10 text-sm"
              />
            </div>

            <div className="mb-4">
              <Label className="text-xs uppercase tracking-widest text-[#d4af37]">
                Comprobante {editing ? "(opcional · solo si quieres reemplazarlo)" : ""}
              </Label>
              <label
                data-testid="pr-proof-upload"
                className="mt-1.5 flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-[#d4af37]/30 rounded-lg cursor-pointer hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-colors"
              >
                {proofPreview ? (
                  <img src={proofPreview} alt="vista previa" className="max-h-40 rounded shadow" />
                ) : editing?.has_proof ? (
                  <>
                    <ImageIcon className="w-8 h-8 text-[#d4af37]" />
                    <span className="text-xs text-neutral-300">Tienes un comprobante subido. Toca aquí para reemplazarlo.</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-[#d4af37]" />
                    <span className="text-xs text-neutral-400">Toca para subir tu comprobante (imagen ≤ 5 MB)</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </label>
            </div>

            <button
              onClick={submit}
              disabled={submitting}
              data-testid="pr-submit"
              className="w-full bg-[#d4af37] hover:bg-[#e6c350] text-black font-bold py-3 rounded-lg uppercase tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {editing ? "Reenviar solicitud" : "Enviar solicitud"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


/**
 * Section displayed inside MyAccount > Mis Pagos tab.
 * Lists the user's payment requests with their status, and an action button
 * to create a new one or edit a rejected one.
 */
export const ClientPaymentRequests = ({ authHeader, prefillServiceId = null }) => {
  const [requests, setRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prefill, setPrefill] = useState(null);

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/users/payment-requests`, { headers: authHeader() }),
        axios.get(`${API}/services`),
      ]);
      setRequests(r.data || []);
      // /api/services returns a DICT keyed by service id with shape:
      //   { id, name_es, name_en, category, price_mxn, price_usd }
      // Normalise it to the array shape PaymentRequestModal expects.
      const raw = s.data || {};
      const arr = Array.isArray(raw)
        ? raw
        : Object.values(raw).map((sv) => ({
            id: sv.id,
            name: sv.name_es || sv.name_en || sv.id,
            price: sv.price_mxn ?? sv.price_usd ?? null,
            currency: sv.price_mxn != null ? "MXN" : (sv.price_usd != null ? "USD" : "MXN"),
            category: sv.category,
          }));
      setServices(arr);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // If a prefillServiceId was passed via URL (?service=<id>), auto-open the
  // payment request modal pre-filled with that service as soon as we have
  // the services list loaded.
  useEffect(() => {
    if (!prefillServiceId || !services.length || modalOpen) return;
    const svc = services.find((s) => s.id === prefillServiceId);
    if (!svc) return;
    setEditing(null);
    setPrefill({ service_id: svc.id, service_label: svc.name });
    setModalOpen(true);
  }, [prefillServiceId, services, modalOpen]);

  const statusMeta = {
    pending: { label: "Pendiente", icon: Clock, cls: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
    approved: { label: "Aprobado", icon: CheckCircle2, cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
    rejected: { label: "Rechazado", icon: XCircle, cls: "border-red-500/40 text-red-300 bg-red-500/10" },
  };

  return (
    <div className="space-y-3" data-testid="client-payment-requests">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-serif text-lg text-white flex items-center gap-2">
          <ReceiptIcon className="w-4 h-4 text-[#d4af37]" />
          Mis solicitudes de pago
        </h3>
        <button
          data-testid="client-new-payment-request"
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="bg-[#d4af37] hover:bg-[#e6c350] text-black font-semibold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"
        >
          <Upload className="w-3.5 h-3.5" /> Subir comprobante
        </button>
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#d4af37] mx-auto" /></div>
      ) : requests.length === 0 ? (
        <div className="glass-dark rounded-xl p-6 text-center text-neutral-500 italic text-sm">
          Aún no has enviado solicitudes de pago. Cuando subas un comprobante, Cristopher lo revisará y registrará el pago.
        </div>
      ) : (
        <div className="space-y-2">
          {requests.map((r) => {
            const meta = statusMeta[r.status] || statusMeta.pending;
            const Icon = meta.icon;
            return (
              <div key={r.id} className="glass-dark rounded-xl p-4" data-testid={`pr-row-${r.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                  <div>
                    <div className="text-white text-sm font-medium">{r.service_label}</div>
                    <div className="text-xs text-neutral-400 mt-0.5">
                      {r.payment_type === "completo" ? "Pago completo" : "Abono parcial"} · ${Number(r.amount).toLocaleString("es-MX")} {r.currency}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${meta.cls}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                </div>

                {r.status === "rejected" && r.rejection_motive && (
                  <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/20 rounded p-2 mb-2">
                    <b>Motivo del rechazo:</b> {r.rejection_motive}
                  </div>
                )}
                {r.notes && (
                  <div className="text-[11px] italic text-neutral-400 mb-2">📝 {r.notes}</div>
                )}

                <div className="flex items-center justify-between text-[10px] text-neutral-500 mt-2">
                  <span>{new Date(r.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</span>
                  {(r.status === "pending" || r.status === "rejected") && (
                    <button
                      data-testid={`pr-edit-${r.id}`}
                      onClick={() => { setEditing(r); setModalOpen(true); }}
                      className="text-[#d4af37] hover:underline inline-flex items-center gap-1"
                    >
                      {r.status === "rejected" ? <RefreshCw className="w-3 h-3" /> : <Edit3 className="w-3 h-3" />}
                      {r.status === "rejected" ? "Corregir y reenviar" : "Editar"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PaymentRequestModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setPrefill(null); }}
        onSubmitted={load}
        authHeader={authHeader}
        services={services}
        editing={editing || prefill}
      />
    </div>
  );
};
